import { useMemo } from 'react';
import { scoring, type TrafficLight } from '@tragfaehigkeit/core';
import { de } from '../../lib/i18n/de';
import { buildScoreResultFromCase } from '../../lib/store/scoreInput';
import type { WizardCaseState } from '../../lib/store/wizard';

interface CaseScoreCardProps {
  caseState: WizardCaseState | undefined;
}

/**
 * Editorial-Score-Karte für die Hub-Seite.
 *
 * Zeigt den aktuellen Total-Score (gewichteter Durchschnitt der sechs
 * Dimensionen), die Ampelfarbe und die Datengrundlage (Konfidenz). Solange
 * weitere Module noch nicht ausgefüllt sind, weist der Hinweis-Satz darauf
 * hin, dass der Score vorläufig ist (Konzept §5.5: <60% = nicht abschließend).
 */
export function CaseScoreCard({ caseState }: CaseScoreCardProps) {
  const t = de.hub.liveScore;
  const result = useMemo(() => buildScoreResultFromCase(caseState), [caseState]);

  const total = result.totalScore as number;
  const confidence = result.totalConfidencePercent as number;
  const isFinal = confidence >= (scoring.CONFIDENCE_FINAL_THRESHOLD as number);

  return (
    <section
      aria-label={t.sectionLabel}
      className="mt-6 mb-10 rounded-sm border border-stone-200 bg-stone-50/60 px-6 py-6 md:px-8 md:py-7"
    >
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {t.sectionLabel}
      </p>

      <div className="mt-4 flex flex-col gap-6 md:flex-row md:items-end md:justify-between md:gap-10">
        <div className="flex items-baseline gap-3">
          <span
            className="font-serif text-6xl font-black tabular-nums tracking-tight text-stone-900 md:text-7xl"
            data-testid="hub-total-score"
          >
            {total}
          </span>
          <span className="font-serif text-base italic text-stone-500">{t.ofLabel}</span>
        </div>

        <div className="flex flex-col items-start gap-2 md:items-end">
          <TrafficBadge light={result.trafficLight} />
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {t.confidenceLabel}: {confidence}% ·{' '}
            <span className={isFinal ? 'text-stone-700' : 'text-stone-500'}>
              {isFinal ? t.confidenceFinal : t.confidencePreliminary}
            </span>
          </p>
        </div>
      </div>

      <p className="mt-5 max-w-prose font-serif text-sm italic text-stone-500">{t.hint}</p>
    </section>
  );
}

function TrafficBadge({ light }: { light: TrafficLight }) {
  const t = de.hub.liveScore;
  const map: Record<TrafficLight, { label: string; dot: string; text: string }> = {
    green: { label: t.trafficGreen, dot: 'bg-emerald-500', text: 'text-emerald-700' },
    yellow: { label: t.trafficYellow, dot: 'bg-amber-500', text: 'text-amber-700' },
    red: { label: t.trafficRed, dot: 'bg-rose-500', text: 'text-rose-700' },
  };
  const v = map[light];
  return (
    <span
      data-testid="hub-traffic-light"
      className={`inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-widest ${v.text}`}
    >
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${v.dot}`} />
      {v.label}
    </span>
  );
}
